import React, { useEffect, useCallback, useMemo } from "react"
import BoardCell from "./BoardCell"
import { useAppSelector } from "@/store/hooks"
import { useAppDispatch } from "@/store/hooks"
import { allMapsImageData, allPieces } from "@/utils/data"
import { mapDesigns } from "@mono/mapDesigns"
import { useRef } from "react"
import {
  setGlobalElement,
  addToastNotification,
  setActiveTrade,
  setBoardPickerMode,
  setBoardPickerPendingCell,
} from "@/store/globalSlice"
import {
  getBoardPickerEligibleOrders,
  getBoardPickerTargetKind,
  getOpponentsOnCell,
  playBoardPickerCard,
} from "@/utils/boardPickerHelpers"
import { store } from "@/store/store"
import { getCurrentPlayer } from "@/utils/gameHelpers"
import {
  canAffordNextHouse,
  canBuyHouseLive,
  canSellHouseLive,
  emitImmediateHouseChange,
} from "@/utils/propmngHelpers"
import { getUserCreds, toggleItemInArray } from "@/utils/utils"
import {
  getTradeCardSelectionType,
  isTradeDraftEditable,
  isTradeUiActive,
} from "@/utils/tradeHelpers"
const BoardCellWrapper = ({ cell }: { cell: ICell }) => {
  const dispatch = useAppDispatch()
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const activeAuctionPropertyOrder = useAppSelector(
    (state) => state.global.myGame?.situation?.activeAuctionPropertyOrder
  )
  const houseBuildMode = useAppSelector((state) => state.global.houseBuildMode)
  // Trade-creation dimming: while the trade modal is open, only properties that
  // can be part of the trade (owned by a participant, no houses) stay bright.
  // Gated on the drawer actually being on screen (isTradeUiActive) and not on
  // the open flag alone: the flag outliving a drawer that has stopped rendering
  // is what used to dim the whole board with nothing left to un-dim it.
  const tradeVisible = useAppSelector(
    (state) =>
      !!state.global.modalData.trade?.visible && isTradeUiActive(state.global)
  )
  const tradeOfferingOrder = useAppSelector(
    (state) => state.global.tradeData.activeTrade?.offeringPlayerOrder
  )
  const tradeOfferedOrder = useAppSelector(
    (state) => state.global.tradeData.activeTrade?.offeredPlayerOrder
  )
  // Only draft trades are still editable, so property clicks on the board should
  // only toggle selection in those states (see isTradeDraftEditable).
  const tradeStatus = useAppSelector((state) => state.global.tradeData.status)
  // Highlight this cell while its matching card is hovered in the trade modal.
  const isTradeHovered = useAppSelector(
    (state) => state.global.tradeData.hoveredPropertyOrder === cell.order
  )
  // Whether this property is currently part of the offer, and from whose side —
  // mirrors the small trade cards so the board rings match the modal colours.
  // Returns a primitive so the cell only re-renders when its own state flips.
  const tradeSelectionType = useAppSelector((state): TradeCardSelectionType => {
    // Same gate as the dimming above, so rings can't be left on the board by a
    // draft that outlives the drawer. Deliberately not the dimming flag itself:
    // a trade viewed from the sidebar highlights its properties without dimming.
    if (!isTradeUiActive(state.global)) return "normal"
    if (!state.global.tradeData.activeTrade) return "normal"
    const myOrder = state.global.myGame?.situation?.players.find(
      (p) => p.id === getUserCreds().email
    )?.order
    if (myOrder === undefined) return "normal"
    return getTradeCardSelectionType(state.global.tradeData, cell, myOrder)
  })
  // Whether this property can be clicked to buy/sell a house in the current
  // build mode. Returns a primitive so the cell only re-renders when it flips.
  const isBuildEligible = useAppSelector((state) => {
    const g = state.global
    if (!g.houseBuildMode || !g.myGame) return false
    const me = getCurrentPlayer(g.myGame)
    if (!me) return false
    return g.houseBuildMode === "buy"
      ? canBuyHouseLive(g.myGame, me.order, cell.order)
      : canSellHouseLive(g.myGame, me.order, cell.order)
  })

  // Price shown on the cell during build mode: the cost of the next house when
  // buying, or the refund for the last house when selling. Null when N/A.
  const buildPrice = useAppSelector((state) => {
    const g = state.global
    const mode = g.houseBuildMode
    if (!mode || !g.myGame?.situation) return null
    const items = cell.propertyDetails?.rentData?.upgradeItems
    if (!items) return null
    const current =
      g.myGame.situation.housesData.find(
        (item) => item.propertyOrder === cell.order
      )?.houses || 0
    if (mode === "buy") {
      if (current >= items.length) return null
      return items[current].price
    }
    if (current < 1) return null
    return items[current - 1].price / 2
  })

  // The rent this property would charge after the build/sell change.
  const newRent = useAppSelector((state) => {
    const g = state.global
    const mode = g.houseBuildMode
    if (!mode || !g.myGame?.situation) return null
    const rentData = cell.propertyDetails?.rentData
    if (!rentData) return null
    const items = rentData.upgradeItems
    const current =
      g.myGame.situation.housesData.find(
        (item) => item.propertyOrder === cell.order
      )?.houses || 0
    if (mode === "buy") {
      if (current >= items.length) return null
      return items[current].rent
    }
    if (current < 1) return null
    const newHouses = current - 1
    return newHouses === 0 ? rentData.base : items[newHouses - 1].rent
  })

  const handleBuildClick = useCallback(() => {
    const g = store.getState().global
    if (!g.houseBuildMode || !g.myGame) return
    const me = getCurrentPlayer(g.myGame)
    if (!me) return
    const eligible =
      g.houseBuildMode === "buy"
        ? canBuyHouseLive(g.myGame, me.order, cell.order)
        : canSellHouseLive(g.myGame, me.order, cell.order)
    if (!eligible) return
    if (
      g.houseBuildMode === "buy" &&
      !canAffordNextHouse(g.myGame, me.order, cell.order)
    ) {
      dispatch(
        addToastNotification({
          type: "general",
          message: "Not enough money to build a house",
          messageType: "error",
        })
      )
      return
    }
    emitImmediateHouseChange(g, cell.order, g.houseBuildMode === "buy" ? 1 : -1)
  }, [cell.order, dispatch])
  // Board-picker mode for the board-targeted action cards (forced auction,
  // demolish house, double-price purchase, rent increase, swap position).
  // Only the cells the active card can legally target stay bright/clickable —
  // properties for the first four, tiles opponents are standing on for the
  // swap.
  const boardPickerMode = useAppSelector(
    (state) => state.global.boardPickerMode
  )
  const isBoardPickerEligible = useAppSelector((state) => {
    const g = state.global
    if (!g.boardPickerMode || !g.myGame) return false
    return getBoardPickerEligibleOrders(
      g.myGame,
      g.boardPickerMode.cardId,
      g.boardPickerMode.playerOrder
    ).includes(cell.order)
  })
  const handleBoardPickerSelect = useCallback(() => {
    const g = store.getState().global
    if (!g.boardPickerMode || !g.myGame) return
    const { cardId, playerOrder } = g.boardPickerMode
    const eligible = getBoardPickerEligibleOrders(
      g.myGame,
      cardId,
      playerOrder
    ).includes(cell.order)
    if (!eligible) return

    if (getBoardPickerTargetKind(cardId) === "player") {
      const opponents = getOpponentsOnCell(g.myGame, cell.order, playerOrder)
      if (!opponents.length) return
      // Tokens share a tile often enough (jail, Go, a crowded street) that
      // guessing which of them was meant would swap the player with the wrong
      // opponent, so the banner takes over and asks.
      if (opponents.length > 1) {
        dispatch(setBoardPickerPendingCell(cell.order))
        return
      }
      playBoardPickerCard(g.myGame.id, cardId, playerOrder, {
        kind: "player",
        targetedPlayerOrder: opponents[0].order,
      })
    } else {
      playBoardPickerCard(g.myGame.id, cardId, playerOrder, {
        kind: "property",
        propertyOrder: cell.order,
      })
    }
    dispatch(setBoardPickerMode(null))
  }, [cell.order, dispatch])

  const owner = useAppSelector((state) =>
    state.global.myGame?.situation?.players.find((player: IPlayer) =>
      player.properties.includes(cell.order)
    )
  )
  const myHousesCount = useAppSelector(
    (state) =>
      state.global.myGame?.situation?.housesData.find(
        (item) => item.propertyOrder === cell.order
      )?.houses || 0
  )
  const mortgagedProperties = useAppSelector(
    (state) => state.global.myGame?.situation?.mortgagedProperties
  )
  const mortgageAnimations = useAppSelector(
    (state) =>
      state.global.animationData.propChangesAppliedAnimation.mortgageAnimations
  )
  const doubleRentProperties = useAppSelector(
    (state) => state.global.myGame?.situation?.doubleRentProperties
  )
  // const pieceMovementAnimationRunning = false
  const pieceMovementAnimationRunning = useAppSelector(
    (state) => state.global.pieceMovementAnimationRunning
  )
  const houseAnimations = useAppSelector(
    (state) =>
      state.global.animationData.propChangesAppliedAnimation.houseAnimations
  )
  const isForceDowngradeTarget = useAppSelector(
    (state) => state.global.forceDowngradeHighlightOrder === cell.order
  )
  const mapImageData = allMapsImageData.find((map) => map.mapId === mapId)
  const cellImageData = mapImageData?.cellImages.find(
    (cellImageData) => cellImageData.order === cell.order
  )
  const ownerImage = useAppSelector((state) => {
    const owner = state.global.myGame?.situation?.players.find(
      (player: IPlayer) => player.properties.includes(cell.order)
    )
    return allPieces.find((piece) => piece.id === owner?.pieceId)
  })

  const myMap = mapDesigns.find((map) => map.id === mapId)
  const myGroup = myMap?.groups.find(
    (group) => group.id === cell.propertyDetails?.groupId
  )
  const jailOverlayRef = useRef(null)
  const rowSize = Math.floor((myMap?.cells?.length || 0) / 4)
  const myRow = Math.floor(cell.order / rowSize)
  const rowClass = "row" + myRow
  const higherZIndex = useAppSelector(
    (state) =>
      state.global.myGame?.situation?.players.find(
        (player) => player.id === state.global.myGame?.situation?.turn
      )?.position === cell.order
  )

  useEffect(() => {
    if (jailOverlayRef.current) {
      // debugger;
      dispatch(setGlobalElement({ jailOverlay: jailOverlayRef.current }))
    }
  }, [dispatch])
  const lowBrightness = useCallback((): boolean => {
    if (!activeAuctionPropertyOrder) return false
    if (activeAuctionPropertyOrder === cell.order) return false
    return true
  }, [cell.order, pieceMovementAnimationRunning])
  // Board picking asks the player to weigh the ineligible tiles too
  // (whose house is worth demolishing, which unsold one is worth auctioning),
  // so that mode dims gently instead of blacking the rest of the board out.
  const brightnessClass = boardPickerMode
    ? "soft-low-brightness"
    : "low-brightness"

  const isMortgaged = mortgagedProperties?.includes(cell.order)
  const shouldShowMortgageOverlay = useMemo(() => {
    isMortgaged ||
      mortgageAnimations.some(
        (item) =>
          item.type === "un-mortgaged" && item.propertyOrder === cell.order
      )
  }, [cell.order, isMortgaged, mortgageAnimations])

  const myRemoveAnimations = useMemo(
    () =>
      houseAnimations
        .filter((item) => item.type === "remove")
        .filter((item) => item.propertyOrder === cell.order),
    [cell.order, houseAnimations]
  )
  // True while this cell has a house being added/removed, so it can be lifted
  // above its neighbours and the board centre for the duration of the animation.
  const hasHouseAnimation = useMemo(
    () => houseAnimations.some((item) => item.propertyOrder === cell.order),
    [cell.order, houseAnimations]
  )
  const shouldShowDoubleRentLabel = useMemo(
    () =>
      doubleRentProperties?.find((item) => item.propertyOrder === cell.order) ||
      null,
    [cell.order, doubleRentProperties]
  )

  // Calculate corner number based on map size
  const cornerNumber = useMemo(() => {
    if (!myMap?.cells) return null
    const totalCells = myMap.cells.length
    const cornerPositions = [
      0, // Corner 0
      totalCells / 4, // Corner 1
      totalCells / 2, // Corner 2
      (totalCells * 3) / 4, // Corner 3
    ]
    const cornerIndex = cornerPositions.findIndex((pos) => pos === cell.order)
    return cornerIndex >= 0 ? cornerIndex : null
  }, [cell.order, myMap?.cells])

  // A property can join the trade when the create-trade modal is open, it is
  // owned by one of the two traders, and it carries no houses (houses can't be
  // traded). This is the same set that stays bright while the modal is open.
  const isTradeEligible =
    !!tradeVisible &&
    !!owner &&
    (owner.order === tradeOfferingOrder || owner.order === tradeOfferedOrder) &&
    (myHousesCount || 0) === 0
  const tradeSelectMode = isTradeEligible && isTradeDraftEditable(tradeStatus)

  // Toggle this property in/out of the offer, on whichever side its owner sits.
  // Mirrors the click handler on the small trade cards in the modal.
  const handleTradeToggle = useCallback(() => {
    const g = store.getState().global
    const activeTrade = g.tradeData.activeTrade
    if (!activeTrade) return
    if (!isTradeDraftEditable(g.tradeData.status)) return
    const ownerPlayer = g.myGame?.situation?.players.find((p) =>
      p.properties.includes(cell.order)
    )
    if (!ownerPlayer) return
    if (ownerPlayer.order === activeTrade.offeringPlayerOrder) {
      dispatch(
        setActiveTrade({
          givenProperties: toggleItemInArray(
            activeTrade.givenProperties,
            cell.order
          ),
        })
      )
    } else if (ownerPlayer.order === activeTrade.offeredPlayerOrder) {
      dispatch(
        setActiveTrade({
          takenProperties: toggleItemInArray(
            activeTrade.takenProperties,
            cell.order
          ),
        })
      )
    }
  }, [cell.order, dispatch])

  return (
    <BoardCell
      rowClass={rowClass}
      tradeSelectMode={tradeSelectMode}
      tradeSelectionType={tradeSelectionType}
      isTradeHovered={isTradeHovered}
      onTradeToggle={handleTradeToggle}
      myGroup={myGroup}
      shouldShowDoubleRentLabel={!!shouldShowDoubleRentLabel}
      shouldShowMortgageOverlay={false}
      isMortgaged={false}
      lowBrightness={
        boardPickerMode
          ? !isBoardPickerEligible
          : houseBuildMode
          ? !isBuildEligible
          : tradeVisible
          ? !isTradeEligible
          : activeAuctionPropertyOrder !== cell.order &&
            activeAuctionPropertyOrder !== null
      }
      boardPickerSelectMode={!!boardPickerMode}
      isBoardPickerEligible={isBoardPickerEligible}
      onBoardPickerSelect={handleBoardPickerSelect}
      houseBuildModeActive={!!houseBuildMode}
      houseBuildMode={houseBuildMode || null}
      isBuildEligible={isBuildEligible}
      buildPrice={buildPrice}
      newRent={newRent}
      onBuildClick={handleBuildClick}
      brightnessClass={brightnessClass}
      cellImageData={cellImageData}
      myHousesCount={myHousesCount || 0}
      ownerImage={ownerImage}
      higherZIndex={higherZIndex}
      hasHouseAnimation={hasHouseAnimation}
      myHouseRemoveAnimations={myRemoveAnimations}
      cell={cell}
      cornerNumber={cornerNumber}
      isForceDowngradeTarget={isForceDowngradeTarget}
    ></BoardCell>
  )
}

export default React.memo(BoardCellWrapper)
